import * as React from "react";
import styled from "styled-components";
import {
  DataGridRow,
  DataGridCell,
  Main,
  fontWeight,
  color,
  spacing,
} from "@conserveit/blocks";
import { Zone } from "../../../../context/reports/comfort/types";
import { roundDatum } from "../../../../utils/numbers";
import { ComfortModal } from "./ComfortModal";

interface Props {
  zone: Zone;
}

export interface ModalSettings {
  isOpen: boolean;
  equipId?: string;
}

const StyledMain = styled(Main) <{ pctDiscomfort?: Zone["pctDiscomfort"] }>`
  font-weight: ${(props) =>
    props.pctDiscomfort && props.pctDiscomfort >= 80
      ? fontWeight.bold
      : fontWeight.normal};
  font-size: 13px;
  line-height: 1.07;
  text-transform: none;
  color: ${(props) =>
    props.pctDiscomfort && props.pctDiscomfort >= 80
      ? color.theme.semantic.destructive.primary.background
      : color.theme.typography.ink};
`;

const Row = styled(DataGridRow)`
  /*  The reason we target DataGridRow is because we want to show
  the cell when the row is rolled over */
  .action-cell .DataGrid-CellWrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .action-link {
    i {
      font-style: italic;
    }
  }
  .insights-link {
    margin-right: ${spacing.default};
  }

  .skyspark-link {
    margin-left: ${spacing.default};
  }

  .action-link,
  .insights-link, 
  .skyspark-link {
    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }
`;

function getInsightsUrl(zoneName: string) {
  return `/analysis/insights?analysisSearch=${zoneName}&category=comfort&daysRange=30`;
}

function getSkysparkTrendUrl(zoneUrl: string) {
  return `${zoneUrl}`;
}

export const SummaryRow = React.memo<Props>(({ zone }) => {
  const [modalSettings, setModalSettings] = React.useState<ModalSettings>({
    isOpen: false,
    equipId: undefined,
  });
  const openModal = React.useCallback(
    (equipId: string) => {
      setModalSettings({
        isOpen: true,
        equipId,
      });
    },
    [modalSettings]
  );
  return (
    <>
      <Row key={zone.id}>
        <DataGridCell>
          <StyledMain>{zone.name}</StyledMain>
        </DataGridCell>
        <DataGridCell>
          <StyledMain pctDiscomfort={zone.pctDiscomfort}>
            {zone.pctDiscomfort &&
              zone.pctDiscomfort !== 0 &&
              `${roundDatum(zone.pctDiscomfort)}%`}
          </StyledMain>
        </DataGridCell>
        <DataGridCell>
          <StyledMain>
            {zone.hoursDiscomfort &&
              zone.hoursDiscomfort !== 0 &&
              roundDatum(zone.hoursDiscomfort)}
          </StyledMain>
        </DataGridCell>
        <DataGridCell>
          <StyledMain>
            {zone.hoursHot !== 0 && roundDatum(zone.hoursHot)}
          </StyledMain>
        </DataGridCell>
        <DataGridCell>
          <StyledMain>
            {zone.hoursCold !== 0 && roundDatum(zone.hoursCold)}
          </StyledMain>
        </DataGridCell>
        <DataGridCell className="action-cell">
          <a
            className="insights-link"
            target="_blank"
            href={getInsightsUrl(zone.name)}
          >
            View Insights
          </a>
          {/* TODO: since the endpoint is not yet ready, so we are using an example fake equip id for now  */}
          <a className="action-link" onClick={() => openModal(zone.id)}>
            View <i>Open</i> Actions
          </a>
          <a
            className="skyspark-link"
            target="_blank"
            href={getSkysparkTrendUrl(zone.skysparkTrendURL)}
          >
            Trend View
          </a>
        </DataGridCell>
      </Row>

      {modalSettings.isOpen && modalSettings.equipId && (
        <ComfortModal
          equipIds={[modalSettings.equipId]}
          isOpen={modalSettings.isOpen}
          setModalVisibility={setModalSettings}
        />
      )}
    </>
  );
});
